import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { motion } from "framer-motion";

const fmtDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }).toUpperCase()
    : "";

export default function ArticlePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api
      .get(`/articles/${id}`)
      .then((r) => setArticle(r.data))
      .catch(() => setNotFound(true));
  }, [id]);

  return (
    <div data-testid="article-page">
      <Navbar />
      <main className="pt-28 pb-20 px-4 sm:px-8 lg:px-16 min-h-screen">
        {notFound ? (
          <div className="py-32 text-center">
            <p className="font-heading font-extrabold uppercase text-4xl text-slate-200">Story not found</p>
            <button
              data-testid="article-back-btn"
              onClick={() => navigate("/", { state: { scrollTo: "#coverage" } })}
              className="mt-8 text-xs font-mono uppercase tracking-[0.2em] text-amber-400 hover:text-amber-300"
            >
              ← Back to coverage
            </button>
          </div>
        ) : !article ? (
          <p className="py-32 text-center text-xs font-mono uppercase tracking-[0.25em] text-slate-500" data-testid="article-loading">
            Pulling the dispatch…
          </p>
        ) : (
          <motion.article
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-4xl mx-auto"
          >
            <button
              data-testid="article-back-btn"
              onClick={() => navigate("/", { state: { scrollTo: "#coverage" } })}
              className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.2em] text-slate-400 hover:text-amber-400 transition-colors mb-10"
            >
              <ArrowLeft size={14} /> Back to coverage
            </button>
            <div className="flex flex-wrap items-center gap-4 mb-6">
              <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-amber-400 border border-amber-400/40 px-2.5 py-1">
                {article.category}
              </span>
              <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">
                {fmtDate(article.created_at)} · {article.read_time} · {article.author}
              </span>
            </div>
            <h1
              data-testid="article-title"
              className="font-heading font-extrabold uppercase tracking-tighter leading-[0.95] text-4xl sm:text-6xl"
            >
              {article.title}
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed font-serif italic">
              {article.excerpt}
            </p>
            <div className="my-10 clipped-frame border border-white/10" data-testid="article-cover-frame">
              <img src={article.cover_url} alt={article.title} className="w-full h-auto block" data-testid="article-cover-image" />
            </div>
            <div className="space-y-6 max-w-3xl" data-testid="article-body">
              {article.body.split("\n\n").map((p, i) => (
                <p key={i} className="text-sm sm:text-base text-slate-300 leading-relaxed">
                  {p}
                </p>
              ))}
            </div>
            <p className="mt-12 text-[10px] font-mono uppercase tracking-[0.25em] text-slate-600">
              — Filed by {article.author} for Ekal Sports Media
            </p>
          </motion.article>
        )}
      </main>
      <Footer />
    </div>
  );
}
